import {List, Map} from 'immutable'
import {v4 as uuidV4} from 'uuid'

import {deputyManageableItems} from './corpMaxDeputyTools'
import {firstName, humanizedFullName, lastName} from './nameTools'
import {findResourceByAttributeId, findResourceById} from './plan_data/userData'
import {compareCaseInsensitiveStrings} from './textTools'
import {SUGGESTED_DEPUTIES} from '../graphql/queries/deputy'

export const FUNERAL_PREFERENCES_ITEM_SLUG = 'funeral-or-memorial-preferences'

const isVaultExtensionPermissions = ({firstGroupId, vaultExtension}) => (
  Boolean(firstGroupId) && vaultExtension?.get('id') === firstGroupId
)

export const showsSuggestedDeputies = ({firstGroupId, itemResponse, vaultExtension} = {}) => (
  itemResponse?.get('slug') === FUNERAL_PREFERENCES_ITEM_SLUG ||
  isVaultExtensionPermissions({firstGroupId, vaultExtension})
)

export const suggestedDeputiesEverplanId = ({itemResponse, vaultExtension} = {}) => (
  itemResponse?.get('everplan-id') || vaultExtension?.get('everplan-id')
)

export const suggestedDeputiesRefetchQueries = ({itemResponse = Map(), ownEverplan = true} = {}) => {
  const everplanId = Number.parseInt(itemResponse.get('everplan-id'), 10)

  if (!ownEverplan || itemResponse.get('slug') !== FUNERAL_PREFERENCES_ITEM_SLUG || !Number.isFinite(everplanId))
    return []

  return [{query: SUGGESTED_DEPUTIES, variables: {everplanId}}]
}

export const removeCachedSuggestedDeputy = ({cache, contactId, everplanId}) => {
  const everplanIdInt = Number.parseInt(everplanId, 10)

  if (!Number.isFinite(everplanIdInt))
    return

  cache.updateQuery(
    {query: SUGGESTED_DEPUTIES, variables: {everplanId: everplanIdInt}},
    data => data && {suggestedDeputies: data.suggestedDeputies.filter(contact => contact.id !== contactId)}
  )
}

export const existingPermissionOptions = [
  {
    label: 'Share now',
    selectedLabel: 'Share now',
    value: 'read'
  },
  {
    label: 'Share after death',
    selectedLabel: 'After death',
    value: 'deferred_read'
  },
  {
    label: 'Do not share',
    selectedLabel: 'Do not share',
    value: 'never'
  }
]

export const bulkPermissionOptions = [existingPermissionOptions[0], existingPermissionOptions[1]]

export const findSpecificItemDeputies = ({specificItemsDeputies, firstGroupId}) => (
  findResourceById({resourceList: specificItemsDeputies, id: firstGroupId}).get('deputies', List())
)

// used to find a deputy permission within a singular specificItemDeputies
const findSpecificItemDeputyPermission = ({specificItemDeputies, deputyId}) => (
  findResourceByAttributeId({
    resourceList: specificItemDeputies,
    attribute: 'ownership-id',
    id: deputyId
  }).get('permission', Map())
)

// Used to find a deputy permission within all specificItemsDeputies
const findDeputyPermission = ({specificItemsDeputies, firstGroupId, deputyId}) => {
  if (specificItemsDeputies.isEmpty())
    return Map()

  const specificItemDeputies = findSpecificItemDeputies({specificItemsDeputies, firstGroupId})

  return findSpecificItemDeputyPermission({
    specificItemDeputies,
    deputyId
  })
}

const defaultSpecificItemDeputyPermissions = ({itemResponse, specificItemsDeputies = List(), deputyId = null}) => {
  const firstGroupIds = itemResponse.get('responseGroupMap').keySeq().toList()

  return firstGroupIds.reduce((collection, firstGroupId) => {
    const sharedAttributes = {
      itemId: itemResponse.get('item-id'),
      kitId: itemResponse.get('kit-id'),
      firstGroupId
    }
    const existingPermission = findDeputyPermission({specificItemsDeputies, firstGroupId, deputyId})

    if (existingPermission.isEmpty()) {
      const defaultPermissionOption = deputyId ?
        {...existingPermissionOptions[2]} :
        {...existingPermissionOptions[0]}

      return collection.set(firstGroupId, {...defaultPermissionOption, ...sharedAttributes})
    } else {
      var permissionOption = existingPermissionOptions.find(option => existingPermission.get('permission-type') === option.value)

      return collection.set(firstGroupId, {...permissionOption, ...sharedAttributes, id: existingPermission.get('id')})
    }
  }, Map())
}

const defaultVaultExtensionPermission = ({specificItemsDeputies, vaultExtension, deputyId = null}) => {
  const dictionary = Map()

  if (vaultExtension.isEmpty() || specificItemsDeputies.isEmpty()) {
    return dictionary
  } else {
    const firstGroupId = vaultExtension.get('id')
    let sharedAttributes = {
      itemId: firstGroupId,
      kitId: firstGroupId,
      firstGroupId
    }
    const existingPermission = findDeputyPermission({specificItemsDeputies, firstGroupId, deputyId})

    if (existingPermission.isEmpty()) {
      return dictionary.set(firstGroupId, {...existingPermissionOptions[2], ...sharedAttributes})
    } else {
      const permissionOption = existingPermissionOptions.find(option => existingPermission.get('permission-type') === option.value)

      sharedAttributes = {...sharedAttributes, kitId: existingPermission.get('kit-id')}

      return dictionary.set(firstGroupId, {...permissionOption, ...sharedAttributes, id: existingPermission.get('id')})
    }
  }
}

// Used for displaying the default values for all permissions for an existing deputy
export const defaultDeputyPermissions = ({specificItemsDeputies, deputyId, itemsWithResponsesByCategory, vaultExtension = Map()}) => {
  const itemResponses = itemsWithResponsesByCategory.map(category => category.get('items')).flatten(true)

  const vaultExtensionPermissions = defaultVaultExtensionPermission({
    deputyId,
    specificItemsDeputies,
    vaultExtension
  })

  return itemResponses.reduce((collection, itemResponse) => collection.merge(
    defaultSpecificItemDeputyPermissions({
      deputyId,
      itemResponse,
      specificItemsDeputies
    })
  ), vaultExtensionPermissions).toJS()
}

// Used for displaying the default values for all permissions for new deputy
export const defaultNewDeputyPermissions = (itemsWithResponsesByCategory, vaultExtension = Map()) => {
  const itemResponses = itemsWithResponsesByCategory.map(category => category.get('items')).flatten(true)
  const dictionary = vaultExtension.isEmpty() ?
    vaultExtension :
    Map({
      [vaultExtension.get('id')]: Map({
        ...existingPermissionOptions[0],
        firstGroupId: vaultExtension.get('id'),
        itemId: vaultExtension.get('id'),
        kitId: uuidV4()
      })
    })

  return itemResponses.reduce((collection, itemResponse) => collection.merge(defaultSpecificItemDeputyPermissions({itemResponse})), dictionary).toJS()
}

const defaultSpecificItemDraftDeputyPermissions = ({
  itemResponse,
  permissions
}) => {
  const firstGroupIds = itemResponse.get('responseGroupMap').keySeq().toList()

  return firstGroupIds.reduce((collection, firstGroupId) => {
    const sharedAttributes = {
      itemId: itemResponse.get('item-id'),
      kitId: itemResponse.get('kit-id'),
      firstGroupId
    }

    const existingPermission = permissions[firstGroupId] ?
      Map(permissions[firstGroupId]) :
      Map()

    if (existingPermission.isEmpty()) {
      const defaultPermissionOption = {...existingPermissionOptions[2]}

      return collection.set(
        firstGroupId,
        {...defaultPermissionOption, ...sharedAttributes}
      )
    } else {
      const permissionOption = existingPermissionOptions.find(option =>
        existingPermission.get('value') === option.value
      )

      return collection.set(
        firstGroupId,
        {
          ...permissionOption,
          ...sharedAttributes
        }
      )
    }
  }, Map())
}

const defaultVaultExtensionDraftDeputyPermission = ({
  permissions,
  vaultExtension
}) => {
  const dictionary = Map()

  if (vaultExtension.isEmpty() || Object.keys(permissions).length === 0) {
    return dictionary
  } else {
    const firstGroupId = vaultExtension.get('id')
    let sharedAttributes = {
      itemId: firstGroupId,
      kitId: firstGroupId,
      firstGroupId
    }

    const existingPermission = permissions[firstGroupId] ?
      Map(permissions[firstGroupId]) :
      Map()

    if (existingPermission.isEmpty()) {
      return dictionary.set(
        firstGroupId,
        {...existingPermissionOptions[2], ...sharedAttributes}
      )
    } else {
      const permissionOption = existingPermissionOptions.find(option =>
        existingPermission.get('value') === option.value
      )

      sharedAttributes = {
        ...sharedAttributes,
        kitId: existingPermission.get('kitId')
      }

      return dictionary.set(
        firstGroupId,
        {
          ...permissionOption,
          ...sharedAttributes
        }
      )
    }
  }
}

export const defaultDraftDeputyPermissions = ({
  itemsWithResponsesByCategory,
  permissions,
  vaultExtension = Map()
}) => {
  const itemResponses = itemsWithResponsesByCategory.map(category =>
    category.get('items')
  ).flatten(true)

  const vaultExtensionPermissions = defaultVaultExtensionDraftDeputyPermission({
    permissions,
    vaultExtension
  })

  return itemResponses.reduce((collection, itemResponse) => collection.merge(
    defaultSpecificItemDraftDeputyPermissions({itemResponse, permissions})
  ), vaultExtensionPermissions).toJS()
}

// Used for updating all permissions for a new or existing deputy
export const formatDeputyPermissions = (updatedPermissions, vaultExtension = Map()) => {
  const changedPermissions = Object.values(updatedPermissions).filter(updatedPermission => updatedPermission.itemId)
  return changedPermissions.map(changedPermission => ({
    firstGroupId: changedPermission.firstGroupId,
    permissionType: changedPermission.value,
    id: changedPermission.id,
    itemId: changedPermission.itemId,
    level: vaultExtension.get('id') === changedPermission.firstGroupId ? 'vault_extension' : 'specific_item',
    kitId: changedPermission.kitId
  }))
}

// Used for displaying the default values for existing deputies specific item permissions
export const defaultSpecificItemDeputiesPermissionValues = ({deputiesWithPermission}) => (
  deputiesWithPermission.reduce(
    (permissions, deputy) => {
      const permission = deputy.getIn(['permission', 'permission-type'])

      return permissions.set(deputy.get('ownership-id'), existingPermissionOptions.find(existingPermissionOption => permission === existingPermissionOption.value))
    }, Map()).toJS()
)

// Used for updating permissions for existing deputies for a specific item
export const formatExistingDeputiesPermission = ({newPermissions, deputiesWithPermission}) => (
  deputiesWithPermission.reduce((collection, deputy) => (
    collection.push({
      id: deputy.get('ownership-id'),
      permissions: [{
        id: deputy.get('permission').get('id'),
        permissionType: newPermissions[deputy.get('ownership-id')].value
      }]
    })
  ), List()).toArray()
)

// Used for updating multiple deputies with the same permission for a specific item
export const formatSelectedDeputiesPermission = ({selectedPermissionType, selectedDeputies, deputiesWithPermission, firstGroupId, itemResponse, vaultExtension = Map()}) => (
  selectedDeputies.reduce((collection, deputy) => {
    const existingPermission = findSpecificItemDeputyPermission({specificItemDeputies: deputiesWithPermission, deputyId: deputy.get('id')})

    return collection.push({
      id: parseInt(deputy.get('id'), 10),
      permissions: [
        {
          id: existingPermission.get('id'),
          level: vaultExtension.get('id') === firstGroupId ? 'vault_extension' : 'specific_item',
          firstGroupId,
          permissionType: selectedPermissionType.value,
          itemId: itemResponse?.get('item-id') || firstGroupId,
          kitId: itemResponse?.get('kit-id') || firstGroupId
        }
      ]
    })
  }, List()).toArray()
)

export const permittedDeputies = ({deputies, itemResponse, firmConfig}) => {
  if (firmConfig.isEmpty()) {
    return deputies
  } else {
    return deputies.reduce((collection, deputy) => {
      const manageableItem = deputyManageableItems({isFirmUser: deputy.getIn(['ownable', 'firm-user']), itemResponses: List([itemResponse]), firmConfig})

      return manageableItem.isEmpty() ? collection : collection.push(deputy)
    }, List())
  }
}

export const hasDeputyWithDeferredPermissions = deputies => {
  if (deputies.isEmpty())
    return false
  else
    return deputies.some(deputy => !deputy.getIn(['permissions', 'deferred']).isEmpty())
}

export const isDeferredReadResponseGroup = ({deputyOwnership, firstGroupId}) => {
  if (deputyOwnership)
    return !findResourceByAttributeId({resourceList: deputyOwnership.getIn(['permissions', 'deferred']), attribute: 'first-group-id', id: firstGroupId}).isEmpty()
}

const contactEmails = contact => (contact.get('emails') || List()).filter(Boolean)

const matchesDeputyEmail = ({contact, deputy}) => (
  contactEmails(contact).some(email => email === deputy.getIn(['ownable', 'email']))
)

// Both names are required so that contacts missing a name do not all match each other.
const matchesDeputyName = ({contact, deputy}) => {
  if (!firstName(contact) || !lastName(contact))
    return false

  return compareCaseInsensitiveStrings(
    humanizedFullName(contact),
    humanizedFullName(deputy.get('ownable') || Map())
  ) === 0
}

// The email is what actually identifies a deputy, so a contact that has one is matched on it
// alone -- a contact listing a different address is a different record on purpose. Only when
// the contact has no email at all do we fall back to matching their full name.
export const findContactDeputy = ({contact, deputies = List()}) => {
  const matchesDeputy = contactEmails(contact).isEmpty() ? matchesDeputyName : matchesDeputyEmail

  return deputies.find(deputy => matchesDeputy({contact, deputy})) || Map()
}

// Funeral Preferences and the vault extension are shared separately, so an existing deputy is
// only judged against what the surface they are suggested on can actually share. A sharing modal
// manages one thing at a time, named by its first group id; surfaces without one, like the
// dashboard carousel, cover the arrangements as a whole and count both.
const arrangementPermissionIds = ({firstGroupId, funeralPreferencesItemResponse, vaultExtension}) => {
  if (isVaultExtensionPermissions({firstGroupId, vaultExtension}))
    return List([vaultExtension.get('id')])
  else if (firstGroupId)
    return List([funeralPreferencesItemResponse.get('item-id')]).filter(Boolean)
  else
    return List([funeralPreferencesItemResponse.get('item-id'), vaultExtension.get('id')]).filter(Boolean)
}

export const showExistingDeputy = ({contact, firstGroupId, itemResponses = List(), vaultExtension = Map(), deputies}) => {
  const contactDeputy = findContactDeputy({contact, deputies})
  const funeralPreferencesItemResponse = itemResponses.find(itemResponse => itemResponse.get('slug') === FUNERAL_PREFERENCES_ITEM_SLUG) || Map()
  const permissionIds = arrangementPermissionIds({firstGroupId, funeralPreferencesItemResponse, vaultExtension})

  // Without Funeral Preferences or a vault extension there is nothing to check the deputy's
  // permissions against, so we cannot tell that they are missing arrangement access.
  if (contactDeputy.isEmpty() || permissionIds.isEmpty())
    return false

  const deputyPermissions = contactDeputy.getIn(['permissions', 'read'], List()).concat(contactDeputy.getIn(['permissions', 'deferred'], List())) || List()

  return permissionIds.some(permissionId => !deputyPermissions.some(permission => permission.get('item-id') === permissionId))
}
